import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { PageBanner } from '../components/PageBanner';
import { ArrowRight, DownloadIcon, ShieldCheck, Target, Users, Landmark } from 'lucide-react';
import { AnimatedSection } from '../components/AnimatedSection';
const stages = [{
  number: 1,
  label: 'DISCOVERY',
  title: 'Discovery',
  description: "The process starts with the organisations we've already worked with, assessed and built relationships with over years of direct engagement.",
  color: '#A69070',
  icon: 'https://cdn.magicpatterns.com/uploads/36y1gaAJK4XxVqN6KPkUXz/1.png'
}, {
  number: 2,
  label: 'RELATIONSHIP GRANT(S)',
  title: 'Relationship grant(s)',
  description: "Firetree conducts in-depth assessment of the organisations' model, governance, financial systems, leadership capacity, and more.",
  color: '#C4342D',
  icon: 'https://cdn.magicpatterns.com/uploads/41jmwrK2GuaDdnUPmnu4S2/3.png'
}, {
  number: 3,
  label: 'LONG-TERM PARTNERSHIP',
  title: 'Long-term partnership',
  description: "Organisations that meet Firetree's criteria are considered for the Fund portfolio. Ongoing support, site visits and learning exchange are part of the relationship.",
  color: '#2D2A26',
  icon: 'https://cdn.magicpatterns.com/uploads/wAJWwCwML3Y2ghHmEu7pky/2.png'
}, {
  number: 4,
  label: 'FUND PORTFOLIO',
  title: 'Fund portfolio',
  description: 'After a period of deep partnership funding, established organisations with proven models and strong leadership are invited to join the Fund portfolio for co-funding by values-aligned funders.',
  color: '#243462',
  icon: 'https://cdn.magicpatterns.com/uploads/uh5eoGe3NZJ1prZUi38xps/4.png'
}];
function ClickProgressSection({
  stages
}: {
  stages: typeof stages;
}) {
  const [activeStage, setActiveStage] = useState(0);
  return <div className="py-16 bg-brand-cream" data-id="element-632">
      <div className="w-full max-w-7xl px-4 sm:px-6 lg:px-16 mx-auto" data-id="element-633">
        {/* Progress Bar */}
        <div className="mb-8 sticky top-0 z-50 lg:static bg-brand-cream py-3 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:mx-0 lg:px-0 lg:py-0" data-id="element-634">
          <div className="flex items-center gap-1" data-id="element-635">
            {stages.map((stage, i) => {
            const isActive = i === activeStage;
            const isCompleted = i < activeStage;
            return <button key={stage.number} className={`text-left cursor-pointer transition-all duration-300 ${isActive ? 'flex-[3]' : 'flex-1'} md:flex-1`} onClick={() => setActiveStage(i)} onMouseEnter={() => setActiveStage(i)} data-id="element-636">
                  <div className="text-center mb-3 h-8 flex items-end justify-center overflow-hidden" data-id="element-637">
                    <span className="font-bold uppercase transition-all duration-300 px-1 leading-tight whitespace-nowrap" style={{
                  color: isActive || isCompleted ? stage.color : '#B8A896',
                  fontSize: isActive ? undefined : undefined
                }} data-id="element-638">
                      {/* Mobile: show label for active, number for inactive */}
                      <span className="md:hidden text-base sm:text-lg tracking-[0.05em] sm:tracking-[0.1em]" data-id="element-639">
                        {isActive ? stage.label : stage.number}
                      </span>
                      {/* Desktop: always show full label */}
                      <span className="hidden md:inline text-base tracking-[0.15em]" data-id="element-640">
                        {stage.label}
                      </span>
                    </span>
                  </div>
                  <div className="relative h-2 bg-brand-shape/20 rounded-full overflow-hidden" data-id="element-641">
                    <div className="absolute inset-y-0 left-0 rounded-full transition-all duration-500 ease-out" style={{
                  width: isActive || isCompleted ? '100%' : '0%',
                  backgroundColor: stage.color
                }} data-id="element-642" />
                  </div>
                </button>;
          })}
          </div>
        </div>

        {/* Stage Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-5" data-id="element-643">
          {stages.map((stage, i) => {
          const isActive = i === activeStage;
          const isCompleted = i < activeStage;
          return <button key={stage.number} className="p-3 sm:p-5 lg:p-7 transition-all duration-500 ease-out border text-center cursor-pointer" onClick={() => setActiveStage(i)} onMouseEnter={() => setActiveStage(i)} style={{
            opacity: isActive ? 1 : isCompleted ? 0.85 : 0.5,
            transform: isActive ? 'scale(1.02)' : 'scale(1)',
            borderColor: isActive || isCompleted ? stage.color : 'rgba(166, 144, 112, 0.3)',
            borderTopWidth: isActive ? '3px' : '1px',
            borderTopColor: isActive || isCompleted ? stage.color : 'rgba(166, 144, 112, 0.3)',
            backgroundColor: isActive ? '#F8F3EE' : 'rgba(248,243,238,0.5)'
          }} data-id="element-644">
                <div className="mb-3 sm:mb-4 h-10 sm:h-12 md:h-16 flex items-center justify-center" data-id="element-645">
                  <img src={stage.icon} alt={`Stage ${stage.number}`} className="h-8 sm:h-10 md:h-14 w-auto object-contain transition-opacity duration-500" style={{
                opacity: isActive ? 0.6 : isCompleted ? 0.4 : 0.2
              }} data-id="element-646" />
                </div>
                <p className="text-sm sm:text-sm md:text-base leading-relaxed transition-colors duration-500" style={{
              color: isActive || isCompleted ? '#6B5E4F' : '#B8A896'
            }} data-id="element-647">
                  {stage.description}
                </p>
              </button>;
        })}
        </div>
      </div>
    </div>;
}
export function OurProcessPage() {
  return <div className="pb-24" data-id="element-648">
      <PageBanner title="Our Process" subtitle="The way we build relationships with partners" image="https://cdn.magicpatterns.com/uploads/kZLcB3cCpBTCtwAuWTG7bQ/223EA315-7C60-4F11-A11E-0610D0F7FB19_4_5005_c.jpg" credit="© Firetree Fund" data-id="element-649" />

      {/* Click-Based Progress Section */}
      <ClickProgressSection stages={stages} data-id="element-650" />

      {/* Bottom Section - What we assess */}
      <section className="bg-brand-card/30 py-10 px-4 sm:px-6 lg:px-8" data-id="element-651">
        <div className="max-w-7xl mx-auto" data-id="element-652">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12" data-id="element-653">
            {/* Left column — Sticky title, description & CTAs */}
            <div className="lg:col-span-4" data-id="element-654">
              <div className="lg:sticky lg:top-28" data-id="element-655">
                <AnimatedSection data-id="element-656">
                  <h2 className="text-2xl md:text-3xl lg:text-4xl font-serif text-brand-charcoal mb-6" data-id="element-657">
                    What we assess
                  </h2>
                  <p className="text-brand-brown leading-relaxed mb-4" data-id="element-658">
                    This assessment framework reflects the synthesis of our
                    approach rather than a generic checklist. It should be
                    viewed as a framework built from years of direct operational
                    experience.
                  </p>
                  <p className="text-brand-brown leading-relaxed mb-10 text-sm opacity-80" data-id="element-659">
                    It is not a tool a non-practitioner can apply from the
                    outside, a pass/fail gate, or a substitute for the proximity
                    and trust that comes from years of real partnership.
                  </p>

                  <div className="flex flex-col gap-4" data-id="element-660">
                    <Link to="/who-we-fund" className="inline-flex items-center px-5 py-2.5 border border-brand-red text-brand-red font-semibold hover:bg-brand-red hover:text-white transition-all duration-300 w-fit" data-id="element-661">
                      Our Partners
                      <ArrowRight className="ml-2 w-4 h-4" data-id="element-662" />
                    </Link>
                    <button onClick={() => window.open('https://hgamykbndfrtzaqfftse.supabase.co/storage/v1/object/public/pdfs/Assessment%20Framework%20-%20Firetree%20Fund.pdf', '_blank')} className="inline-flex items-center px-5 py-2.5 border border-brand-charcoal text-brand-charcoal font-semibold hover:bg-brand-charcoal hover:text-white transition-all duration-300 w-fit cursor-pointer" data-id="element-663">
                      Download Assessment Framework
                      <DownloadIcon className="ml-2 w-4 h-4" data-id="element-664" />
                    </button>
                  </div>
                </AnimatedSection>
              </div>
            </div>

            {/* Right column — Stacking cards */}
            <div className="lg:col-span-8" data-id="element-665">
              <div className="flex flex-col gap-6" data-id="element-666">
                {/* Card 1: Non-negotiables */}
                <div className="lg:sticky lg:top-28" style={{
                zIndex: 10
              }} data-id="element-667">
                  <AnimatedSection data-id="element-668">
                    <div className="bg-brand-cream border border-brand-shape/50 overflow-hidden" data-id="element-669">
                      <div className="px-6 lg:px-8 py-3 border-b border-brand-shape/30 border-l-[3px] flex items-center gap-3" style={{
                      borderLeftColor: '#A69070',
                      backgroundColor: 'rgba(166, 144, 112, 0.08)'
                    }} data-id="element-670">
                        <ShieldCheck className="w-5 h-5 text-brand-gold" strokeWidth={2} data-id="element-671" />
                        <span className="text-xs font-bold uppercase tracking-[0.15em] text-brand-gold" data-id="element-672">
                          Non-negotiables
                        </span>
                      </div>
                      <div className="p-6 lg:p-8" data-id="element-673">
                        <ul className="flex flex-col" data-id="element-674">
                          <li className="py-3 border-b border-brand-shape/30 text-base text-brand-brown leading-relaxed" data-id="element-675">
                            Fills a genuine gap not covered by government or
                            existing services
                          </li>
                          <li className="py-3 border-b border-brand-shape/30 text-base text-brand-brown leading-relaxed" data-id="element-676">
                            Decision-makers have deep knowledge of the issue,
                            community and context
                          </li>
                          <li className="py-3 border-b border-brand-shape/30 text-base text-brand-brown leading-relaxed" data-id="element-677">
                            Our existing contextual knowledge of the geography
                          </li>
                          <li className="py-3 text-base text-brand-brown leading-relaxed" data-id="element-678">
                            Appropriate legal registration and structures are in
                            place
                          </li>
                        </ul>
                      </div>
                    </div>
                  </AnimatedSection>
                </div>

                {/* Card 2: Model and impact */}
                <div className="lg:sticky lg:top-[10rem]" style={{
                zIndex: 20
              }} data-id="element-679">
                  <AnimatedSection data-id="element-680">
                    <div className="bg-brand-cream border border-brand-shape/50 overflow-hidden" data-id="element-681">
                      <div className="px-6 lg:px-8 py-3 border-b border-brand-shape/30 border-l-[3px] flex items-center gap-3" style={{
                      borderLeftColor: '#C4342D',
                      backgroundColor: 'rgba(196, 52, 45, 0.05)'
                    }} data-id="element-682">
                        <Target className="w-5 h-5" style={{
                        color: '#C4342D'
                      }} strokeWidth={2} data-id="element-683" />
                        <span className="text-xs font-bold uppercase tracking-[0.15em]" style={{
                        color: '#C4342D'
                      }} data-id="element-684">
                          Model and impact
                        </span>
                      </div>
                      <div className="p-6 lg:p-8" data-id="element-685">
                        <ul className="flex flex-col" data-id="element-686">
                          <li className="py-3 border-b border-brand-shape/30 text-base text-brand-brown leading-relaxed" data-id="element-687">
                            Clear understanding of root causes and
                            problem-solution fit
                          </li>
                          <li className="py-3 border-b border-brand-shape/30 text-base text-brand-brown leading-relaxed" data-id="element-688">
                            Quality and internal coherence of the programme
                            model and activities
                          </li>
                          <li className="py-3 border-b border-brand-shape/30 text-base text-brand-brown leading-relaxed" data-id="element-689">
                            How effectiveness is measured and how the model
                            evolves as a result
                          </li>
                          <li className="py-3 border-b border-brand-shape/30 text-base text-brand-brown leading-relaxed" data-id="element-690">
                            Demonstrable results against the organisation's core
                            objectives
                          </li>
                        </ul>
                      </div>
                    </div>
                  </AnimatedSection>
                </div>

                {/* Card 3: People and culture */}
                <div className="lg:sticky lg:top-[13rem]" style={{
                zIndex: 30
              }} data-id="element-691">
                  <AnimatedSection data-id="element-692">
                    <div className="bg-brand-cream border border-brand-shape/50 overflow-hidden" data-id="element-693">
                      <div className="px-6 lg:px-8 py-3 border-b border-brand-shape/30 border-l-[3px] flex items-center gap-3" style={{
                      borderLeftColor: '#2D2A26',
                      backgroundColor: 'rgba(45, 42, 38, 0.05)'
                    }} data-id="element-694">
                        <Users className="w-5 h-5" style={{
                        color: '#2D2A26'
                      }} strokeWidth={2} data-id="element-695" />
                        <span className="text-xs font-bold uppercase tracking-[0.15em]" style={{
                        color: '#2D2A26'
                      }} data-id="element-696">
                          People and culture
                        </span>
                      </div>
                      <div className="p-6 lg:p-8" data-id="element-697">
                        <ul className="flex flex-col" data-id="element-698">
                          <li className="py-3 border-b border-brand-shape/30 text-base text-brand-brown leading-relaxed" data-id="element-699">
                            Community focus embedded in team DNA
                          </li>
                          <li className="py-3 border-b border-brand-shape/30 text-base text-brand-brown leading-relaxed" data-id="element-700">
                            Capacity to learn, adapt and redefine approaches
                          </li>
                          <li className="py-3 border-b border-brand-shape/30 text-base text-brand-brown leading-relaxed" data-id="element-701">
                            Role clarity, competencies and talent across the
                            organisation
                          </li>
                          <li className="py-3 border-b border-brand-shape/30 text-base text-brand-brown leading-relaxed" data-id="element-702">
                            Leadership quality and management style
                          </li>
                          <li className="py-3 text-base text-brand-brown leading-relaxed" data-id="element-703">
                            Collaborative, open culture with appropriate checks
                            and balances
                          </li>
                        </ul>
                      </div>
                    </div>
                  </AnimatedSection>
                </div>

                {/* Card 4: Governance and operations */}
                <div className="lg:sticky lg:top-[16rem]" style={{
                zIndex: 40
              }} data-id="element-704">
                  <AnimatedSection data-id="element-705">
                    <div className="bg-brand-cream border border-brand-shape/50 overflow-hidden" data-id="element-706">
                      <div className="px-6 lg:px-8 py-3 border-b border-brand-shape/30 border-l-[3px] flex items-center gap-3" style={{
                      borderLeftColor: '#243462',
                      backgroundColor: 'rgba(36, 52, 98, 0.05)'
                    }} data-id="element-707">
                        <Landmark className="w-5 h-5" style={{
                        color: '#243462'
                      }} strokeWidth={2} data-id="element-708" />
                        <span className="text-xs font-bold uppercase tracking-[0.15em]" style={{
                        color: '#243462'
                      }} data-id="element-709">
                          Governance and operations
                        </span>
                      </div>
                      <div className="p-6 lg:p-8" data-id="element-710">
                        <ul className="flex flex-col" data-id="element-711">
                          <li className="py-3 border-b border-brand-shape/30 text-base text-brand-brown leading-relaxed" data-id="element-712">
                            Strong SOPs for programmatic work and child
                            protection protocols
                          </li>
                          <li className="py-3 border-b border-brand-shape/30 text-base text-brand-brown leading-relaxed" data-id="element-713">
                            Robust financial management systems and policies
                          </li>
                          <li className="py-3 border-b border-brand-shape/30 text-base text-brand-brown leading-relaxed" data-id="element-714">
                            Compliance across HR, safeguarding and child
                            protection
                          </li>
                          <li className="py-3 border-b border-brand-shape/30 text-base text-brand-brown leading-relaxed" data-id="element-715">
                            Engaged, diverse board with relevant expertise
                          </li>
                          <li className="py-3 border-b border-brand-shape/30 text-base text-brand-brown leading-relaxed" data-id="element-716">
                            Fundraising strategy aligned with programmatic
                            vision
                          </li>
                          <li className="py-3 text-base text-brand-brown leading-relaxed" data-id="element-717">
                            Composition of Management team and complementarity
                            of skills
                          </li>
                        </ul>
                      </div>
                    </div>
                  </AnimatedSection>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>;
}